import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle2, Clock, AlertTriangle, DollarSign, Receipt, CreditCard, Smartphone, Check, Lock } from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { formatPakistanDate, formatPakistanDateTime } from '../utils/pakistanTime';

export default function CashierClosingModal({ isOpen, onClose, onClosingComplete }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [closingData, setClosingData] = useState(null);
  const [notes, setNotes] = useState('');
  const [confirmStep, setConfirmStep] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadMyStatus();
      setConfirmStep(false);
    }
  }, [isOpen]);

  const loadMyStatus = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const res = await api.get(`/daily-closings/my-status?date=${today}`);
      const data = res?.data || res;
      setClosingData(data);
      if (data?.notes) {
        setNotes(data.notes);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load closing status');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSales = async () => {
    setSubmitting(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const payload = {
        business_date: today,
        notes: notes.trim(),
      };
      const res = await api.post('/daily-closings/close', payload);
      toast.success(res?.message || 'Sales closed successfully for today!');
      if (onClosingComplete) {
        onClosingComplete();
      }
      loadMyStatus();
      setConfirmStep(false);
    } catch (err) {
      toast.error(err.message || 'Failed to close sales');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const todayFormatted = formatPakistanDate(new Date(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const isClosed = closingData?.status === 'CLOSED';

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 sm:p-4 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-lg max-h-[88vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden border border-gray-100 animate-scale-in">
        
        {/* Header (Compact) */}
        <div className={clsx(
          "px-5 py-3 flex-shrink-0 flex items-center justify-between text-white transition-colors",
          isClosed ? "bg-gradient-to-r from-emerald-600 to-teal-700" : "bg-gradient-to-r from-indigo-600 to-blue-700"
        )}>
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/20 backdrop-blur-md">
              {isClosed ? <Lock className="h-4 w-4 text-white" /> : <Clock className="h-4 w-4 text-white" />}
            </div>
            <div>
              <h3 className="text-base font-bold leading-tight">
                {isClosed ? "Daily Closing Completed" : "Daily Cashier Closing"}
              </h3>
              <p className="text-[11px] text-white/80 font-medium">
                {todayFormatted} • {user?.full_name || user?.username || 'Cashier'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-white/80 hover:bg-white/20 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content (Scrollable & Compact) */}
        <div className="p-4 space-y-3 overflow-y-auto flex-1 text-xs">
          {loading ? (
            <div className="py-8 text-center text-gray-500">
              <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent mb-2" />
              <p className="text-xs font-medium">Calculating today's shift totals...</p>
            </div>
          ) : (
            <>
              {/* Status Banner */}
              <div className={clsx(
                "rounded-lg p-2.5 flex items-center gap-2.5 border",
                isClosed 
                  ? "bg-emerald-50/80 border-emerald-200 text-emerald-900" 
                  : "bg-amber-50/80 border-amber-200 text-amber-900"
              )}>
                {isClosed ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                )}
                <div className="text-[11px] leading-snug flex-1">
                  {isClosed ? (
                    <div>
                      <span className="font-bold text-emerald-800">Shift is officially CLOSED</span>
                      <span className="text-emerald-700 ml-1.5">
                        ({closingData?.closed_at ? formatPakistanDateTime(closingData.closed_at) : 'Today'})
                      </span>
                    </div>
                  ) : (
                    <div>
                      <span className="font-bold text-amber-800">Shift is currently OPEN.</span>
                      <span className="text-amber-700 ml-1">Review totals and finalize your day end.</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Summary Metrics */}
              <div className="grid grid-cols-2 gap-2.5">
                <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-2.5">
                  <div className="flex items-center gap-1.5 text-gray-500 text-[11px] font-medium mb-0.5">
                    <Receipt className="h-3.5 w-3.5 text-indigo-500" />
                    <span>Invoices Issued</span>
                  </div>
                  <p className="text-xl font-black text-gray-900">
                    {closingData?.invoice_count || 0}
                  </p>
                </div>

                <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-2.5">
                  <div className="flex items-center gap-1.5 text-emerald-700 text-[11px] font-medium mb-0.5">
                    <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
                    <span>Total Sales</span>
                  </div>
                  <p className="text-xl font-black text-emerald-700">
                    Rs. {Number(closingData?.total_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {/* Payment Breakdown */}
              <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  Payment Method Breakdown
                </h4>
                
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-center justify-between py-1 border-b border-gray-50">
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                      <span>Cash Sales</span>
                    </div>
                    <span className="font-bold text-gray-900">
                      Rs. {Number(closingData?.cash_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  <div className="flex items-center justify-between py-1 border-b border-gray-50">
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                      <span>Card Sales</span>
                    </div>
                    <span className="font-bold text-gray-900">
                      Rs. {Number(closingData?.card_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  <div className="flex items-center justify-between py-1 border-b border-gray-50">
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <div className="h-1.5 w-1.5 rounded-full bg-cyan-500" />
                      <span>Online / Digital Sales</span>
                    </div>
                    <span className="font-bold text-gray-900">
                      Rs. {Number(closingData?.online_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  {Number(closingData?.credit_sales || 0) > 0 && (
                    <div className="flex items-center justify-between py-1 border-b border-gray-50">
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                        <span>Credit / Udhaar Sales</span>
                      </div>
                      <span className="font-bold text-gray-900">
                        Rs. {Number(closingData?.credit_sales || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes Input */}
              {!isClosed && (
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                    Closing Notes / Handover (Optional):
                  </label>
                  <textarea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g., Handed over Rs. 4,000 cash to Admin..."
                    className="w-full rounded-lg border border-gray-200 p-2 text-xs text-gray-800 placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>
              )}

              {isClosed && closingData?.notes && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-2.5 text-xs">
                  <span className="font-semibold text-gray-600 block mb-0.5 text-[11px]">Closing Notes:</span>
                  <p className="text-gray-800 italic">{closingData.notes}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Actions (Compact) */}
        <div className="bg-gray-50 px-5 py-3 border-t border-gray-100 flex-shrink-0 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Close
          </button>

          {!isClosed && (
            confirmStep ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmStep(false)}
                  className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-gray-500 hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCloseSales}
                  disabled={submitting}
                  className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white shadow-md shadow-emerald-600/30 hover:bg-emerald-700 disabled:opacity-50 transition-all"
                >
                  {submitting ? 'Closing...' : 'Confirm & Finalize'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmStep(true)}
                disabled={loading}
                className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-bold text-white shadow-md shadow-indigo-600/30 hover:bg-indigo-700 disabled:opacity-50 transition-all"
              >
                <Lock className="h-3.5 w-3.5" />
                Close My Sales
              </button>
            )
          )}

          {isClosed && (
            <button
              type="button"
              onClick={handleCloseSales}
              disabled={submitting}
              title="Update with any recent changes"
              className="text-xs text-gray-500 hover:text-emerald-700 font-medium underline"
            >
              Re-sync Closing Record
            </button>
          )}
        </div>

      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
